import { API_URL, SHIPPER_GUIDE_API_URL } from 'core/infra/environment'
import { type HttpClientConfig, type RequestInterceptor } from 'core/infra/http/HttpClient'

import sentry from 'legacy/utils/sentry'

import { type UserRepository } from 'auth/infra/userRepository'

export const CARRIER_INVITES_ROOT = '/api/v2/shipperguide/carrier-invites'
export const CARRIER_INVITE_ATTACHMENTS_ROOT = '/api/v2/shipperguide/carrier-invite-attachments'
export const PROPOSALS_ROOT = '/proposals'
export const LOADBOARD_INVITES_ROOT = 'api/v2/rfp/proposals/invites'
export const SESSION_LOADBOARD_INVITES_ROOT = 'api/v2/shipperguide/invites'

type CreateRfpGuideInviteInterceptorParams = {
  inviteUserRepository: UserRepository
  sessionUserRepository: UserRepository
}

export function rewriteCarrierInvitesToProposals(url?: string) {
  if (!url) return url

  if (url.startsWith(CARRIER_INVITES_ROOT)) {
    return `${PROPOSALS_ROOT}${url.slice(CARRIER_INVITES_ROOT.length)}`
  }

  return url
}

function isLoadboardInvitesUrl(url?: string) {
  return Boolean(url?.replace(/^\//, '').startsWith(LOADBOARD_INVITES_ROOT))
}

// The gateway serves the zip download on a dedicated resource so it can apply
// binary handling; the greedy shipperguide proxy cannot. Session traffic only —
// rfp-backend itself still serves bulk_rfp_attachment_download for invite tokens.
export function rewriteBulkAttachmentDownload(url?: string) {
  if (!url) return url

  const match = url.match(
    new RegExp(`^${CARRIER_INVITES_ROOT}/([^/]+)/bulk_rfp_attachment_download/?$`)
  )
  if (!match) return url

  return `${CARRIER_INVITE_ATTACHMENTS_ROOT}/${match[1]}`
}

export function rewriteLoadboardInvitesToShipperGuide(url?: string) {
  if (!url || !isLoadboardInvitesUrl(url)) return url

  const slash = url.startsWith('/') ? '/' : ''
  const rest = url.slice(slash.length + LOADBOARD_INVITES_ROOT.length)

  return `${slash}${SESSION_LOADBOARD_INVITES_ROOT}${rest}`
}

function joinBaseAndPath(baseUrl: string | undefined, path: string | undefined): string {
  const base = (baseUrl ?? '').replace(/\/$/, '')
  const normalizedPath = path?.startsWith('/') ? path : `/${path ?? ''}`

  return `${base}${normalizedPath}`
}

export function createRfpGuideInviteInterceptor({
  inviteUserRepository,
  sessionUserRepository,
}: CreateRfpGuideInviteInterceptorParams): RequestInterceptor {
  return function rfpGuideInviteInterceptor(config: HttpClientConfig) {
    try {
      const inviteToken = inviteUserRepository.getLocalToken()

      if (inviteToken) {
        if (config.headers) {
          config.headers.Authorization = `Bearer ${inviteToken}`
        }

        // Loadboard invites reach rfp-backend through the NONE-auth gateway route,
        // which forwards the invite token verbatim: keep the client's default base.
        if (isLoadboardInvitesUrl(config.url)) {
          return config
        }

        // Only the carrier-invite endpoints exist on rfp-backend's public host; any
        // other path keeps the client's default base so it cannot be sent to a host
        // that does not serve it.
        if (!config.url?.startsWith(CARRIER_INVITES_ROOT)) {
          sentry.log(
            new Error(`RFP Guide client used with a non-carrier-invite path: ${config.url}`)
          )
          return config
        }

        // Axios can ignore a mutated baseURL for absolute-path urls; set a full URL instead.
        config.baseURL = undefined
        config.url = joinBaseAndPath(
          SHIPPER_GUIDE_API_URL,
          rewriteCarrierInvitesToProposals(config.url)
        )

        return config
      }

      config.baseURL = API_URL
      // Logged-in carriers use the authorizer-backed shipperguide mount for
      // loadboard invites; rfp-backend cannot validate the session token itself.
      config.url = rewriteLoadboardInvitesToShipperGuide(config.url)
      config.url = rewriteBulkAttachmentDownload(config.url)

      const sessionToken = sessionUserRepository.getLocalToken()

      if (sessionToken && config.headers) {
        config.headers.Authorization = `Bearer ${sessionToken}`
      }
    } catch (error) {
      sentry.log(new Error('Failed to get RFP auth token', { cause: error }))
    }

    return config
  }
}
